import { useEffect, useRef, useState } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaChevronRight,
  FaStore,
  FaTicketAlt,
} from "react-icons/fa";
import "./EventCard.css";
import { getEventTiers, getLowestDisplayPrice, formatPrice } from "../../services/ticketingService";
import { useAuth } from "../../contexts/AuthContext";

const TZ = "America/Chicago";

function formatEventDate(startAt, endAt) {
  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const options = { month: "short", day: "numeric", timeZone: TZ };
  const startFormatted = startDate.toLocaleDateString("en-US", options);

  const startDay = startDate.toLocaleDateString("en-US", { timeZone: TZ });
  const endDay   = endDate.toLocaleDateString("en-US", { timeZone: TZ });
  if (startDay === endDay) {
    return startFormatted;
  }

  const endFormatted = endDate.toLocaleDateString("en-US", options);
  return `${startFormatted} - ${endFormatted}`;
}

function formatEventTime(startAt, endAt) {
  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const timeOptions = { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ };
  const startTime = startDate.toLocaleTimeString("en-US", timeOptions);
  const endTime   = endDate.toLocaleTimeString("en-US", timeOptions);

  const startDay = startDate.toLocaleDateString("en-US", { timeZone: TZ });
  const endDay   = endDate.toLocaleDateString("en-US", { timeZone: TZ });
  if (startDay === endDay) {
    return `${startTime} - ${endTime}`;
  }

  return `Starts ${startTime}`;
}

function capitalizeEventType(type) {
  if (!type) return "";
  return type
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function EventCard({ event, onClick, initialPrice }) {
  const { customerToken } = useAuth();
  // Use parent-provided price immediately; fall back to fetching if not provided
  const [fetchedPrice, setFetchedPrice] = useState(null);
  const lowestTierPrice = initialPrice !== undefined ? initialPrice : fetchedPrice;
  const headerRef = useRef(null);
  const cardRef = useRef(null);

  const isUpcoming = new Date(event.start_at) > new Date();
  const isOngoing  = new Date(event.start_at) <= new Date() && new Date(event.end_at) >= new Date();

  useEffect(() => {
    // Skip fetch if parent already provided the price
    if (initialPrice !== undefined) return;
    if (!event.event_id) return;
    let mounted = true;
    (async () => {
      try {
        const tiers = await getEventTiers(event.event_id, customerToken);
        if (!mounted) return;
        setFetchedPrice(getLowestDisplayPrice(tiers));
      } catch {
        // non-critical
      }
    })();
    return () => { mounted = false; };
  }, [event.event_id, customerToken, initialPrice]);

  return (
    <div
      ref={cardRef}
      className="event-card"
      onClick={() => onClick(event.slug)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(event.slug);
        }
      }}
    >
      {/* Status Badge */}
      {isOngoing && <div className="event-status-badge ongoing">Happening Now</div>}
      {isUpcoming && <div className="event-status-badge upcoming">Upcoming</div>}

      {/* Hero Image */}
      <div className="event-hero-image">
        <img
          src={event.hero_image_url || "https://store-galleries.s3.us-east-1.amazonaws.com/gallery/7b62d9646ec243a1acbce4c22cf363e4.jpg"}
          alt={event.title}
          loading="lazy"
          decoding="async"
        />
        <div className="event-image-overlay" />
      </div>

      {/* Card Content */}
      <div className="event-card-content">
        <div ref={headerRef} className="event-header-container">
          {event.event_type && (
            <div className="event-type-tag">{capitalizeEventType(event.event_type)}</div>
          )}
          <div className="event-title-summary-container">
            <h3 className="event-title">{event.title}</h3>
            {event.short_summary && (
              <p className="event-summary">{event.short_summary}</p>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="event-details">
          <div className="event-detail-item">
            <FaCalendarAlt className="event-icon" />
            <span>{formatEventDate(event.start_at, event.end_at)}</span>
          </div>
          <div className="event-detail-item">
            <FaClock className="event-icon" />
            <span>{formatEventTime(event.start_at, event.end_at)}</span>
          </div>
          <div className="event-detail-item">
            <FaStore className="event-icon" />
            <span>{event.primary_venue ? event.primary_venue.venue_name : "TBD"}</span>
          </div>
          {lowestTierPrice !== null && (
            <div className="event-detail-item event-detail-item--price">
              <FaTicketAlt className="event-icon" />
              <span className="event-ticket-label">
                {lowestTierPrice === 0 ? "Free Entry" : `From ${formatPrice(lowestTierPrice)}`}
              </span>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className={`event-card-cta ${event.ticketing_enabled ? "event-card-cta--tickets" : ""}`}>
          <span>{event.ticketing_enabled ? "Get Tickets" : "View details"}</span>
          <FaChevronRight />
        </div>
      </div>
    </div>
  );
}

export default EventCard;
